/**
 * Header overflow ("More" menu) handling.
 *
 * Measures how many top-level nav items fit in the available row width and
 * moves the ones that don't fit into the "More" dropdown. Runs only above
 * the 1024px breakpoint, where the horizontal nav is visible (below that,
 * the hamburger drawer is used instead and has no overflow problem since it
 * scrolls vertically).
 *
 * Deliberately does NOT use the flex-wrap + order trick some themes use to
 * let the browser compute wrapping for free. That trick is compact but
 * relies on a subtle side effect (temporarily reordering the DOM to read
 * where the browser wraps a line) that is hard to step through with
 * console.log. This version sums item widths one by one instead: slower to
 * write, much easier to debug, and just as correct for a single-row nav.
 */

const DESKTOP_NAV_QUERY = '(min-width: 1024px)';
const DEBOUNCE_MS = 120;

/**
 * Wraps a function so it only runs once, `wait` milliseconds after the last
 * call. Used here to avoid recalculating on every single pixel of a window
 * resize, which would run the layout math dozens of times per second.
 * @param {() => void} fn
 * @param {number} wait
 * @returns {() => void}
 */
function debounce(fn, wait) {
  let timeoutId;
  return function debounced() {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(fn, wait);
  };
}

/**
 * @param {HTMLElement} nav
 */
function setupHeaderOverflow(nav) {
  const list = nav.querySelector('[data-nav-list]');
  const moreItem = nav.querySelector('[data-nav-more]');
  const moreTrigger = moreItem ? moreItem.querySelector('[data-nav-more-trigger]') : null;
  // The panel is no longer nested inside moreItem — it now lives as a
  // full-width sibling directly in the header, so it can grow the header's
  // own height instead of floating over the page (see header.liquid).
  const morePanel = document.querySelector('[data-nav-more-panel]');

  if (!list || !moreItem || !moreTrigger || !morePanel) return;

  // Items in the order they appear in the menu, so we can always rebuild
  // the same starting point before measuring again.
  const originalOrder = Array.from(list.querySelectorAll(':scope > [data-nav-item]')).filter(
    (item) => item !== moreItem
  );

  /**
   * Puts every item back in the nav row, in its original order, and hides
   * the "More" trigger. This is step 1 of every reflow: start from a known,
   * clean state before measuring anything.
   */
  function resetToDefaultPlacement() {
    originalOrder.forEach((item) => {
      item.classList.remove('site-header__nav-item--in-overflow');
      item.classList.remove('is-open');
      list.insertBefore(item, moreItem);
    });
    morePanel.innerHTML = '';
    moreItem.hidden = true;
  }

  /**
   * Measures the real width of the "More" trigger without letting it flash
   * on screen. We need its width even when it ends up hidden, because we
   * must reserve space for it while deciding whether it's needed at all.
   * @returns {number}
   */
  function measureMoreItemWidth() {
    const wasHidden = moreItem.hidden;
    moreItem.hidden = false;
    moreItem.style.visibility = 'hidden';
    const width = moreItem.getBoundingClientRect().width;
    moreItem.style.visibility = '';
    moreItem.hidden = wasHidden;
    return width;
  }

  function reflow() {
    resetToDefaultPlacement();

    const containerWidth = list.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap || '0');
    const moreWidth = measureMoreItemWidth();

    let cumulativeWidth = 0;
    let firstOverflowIndex = originalOrder.length;

    for (let i = 0; i < originalOrder.length; i += 1) {
      const item = originalOrder[i];
      const itemWidth = item.getBoundingClientRect().width;
      // Add exactly one gap per item after the first — cumulativeWidth
      // already carries forward every gap counted in previous iterations,
      // so we must not add gap * i here on top of it (that double-counts
      // and makes the estimate grow faster than the real layout).
      const gapForThisItem = i === 0 ? 0 : gap;
      const widthSoFar = cumulativeWidth + gapForThisItem + itemWidth;

      // Reserve room for the "More" trigger only while there might still be
      // items after this one — the last item never needs to leave space for it.
      const isLastItem = i === originalOrder.length - 1;
      const reserved = isLastItem ? 0 : moreWidth + gap;

      if (widthSoFar + reserved > containerWidth) {
        firstOverflowIndex = i;
        break;
      }

      cumulativeWidth = widthSoFar;
    }

    if (firstOverflowIndex >= originalOrder.length) {
      // Everything fits: set of visible items equals the full set, nothing
      // to move — same idea as a filter() that matched every element.
      moreItem.hidden = true;
      return;
    }

    moreItem.hidden = false;
    for (let i = firstOverflowIndex; i < originalOrder.length; i += 1) {
      const item = originalOrder[i];
      item.classList.add('site-header__nav-item--in-overflow');
      morePanel.appendChild(item);
    }
  }

  const debouncedReflow = debounce(reflow, DEBOUNCE_MS);

  const resizeObserver = new ResizeObserver(debouncedReflow);
  resizeObserver.observe(nav);

  // Accordion behaviour for items once they are inside the "More" panel:
  // clicking a moved item's own link toggles its nested dropdown, instead
  // of relying on hover (hover-inside-hover is unreliable on touch and on
  // narrow panels).
  morePanel.addEventListener('click', (event) => {
    const link = event.target.closest('.site-header__nav-link');
    if (!link || !morePanel.contains(link)) return;

    const item = link.closest('[data-nav-item]');
    if (!item || !item.querySelector('[data-nav-dropdown]')) return;

    event.preventDefault();
    item.classList.toggle('is-open');
  });

  // Toggle the "More" panel itself, matching the pattern above instead
  // of relying on hover, since the trigger is a <button> not a link.
  moreTrigger.addEventListener('click', () => {
    const isOpen = morePanel.classList.toggle('is-open');
    moreTrigger.setAttribute('aria-expanded', String(isOpen));
  });

  // The panel now lives outside moreItem in the DOM (see header.liquid), so
  // a click has to miss BOTH the trigger and the panel to count as "outside".
  document.addEventListener('click', (event) => {
    if (!moreItem.contains(event.target) && !morePanel.contains(event.target)) {
      morePanel.classList.remove('is-open');
      moreTrigger.setAttribute('aria-expanded', 'false');
    }
  });

  reflow();
}

function init() {
  const nav = document.querySelector('[data-nav]');
  if (!nav) return;

  const desktopQuery = window.matchMedia(DESKTOP_NAV_QUERY);
  if (!desktopQuery.matches) return;

  setupHeaderOverflow(nav);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
